import React, { useState, useEffect } from 'react';
import { Header, Footer } from '../../components';
import storage from '../../utils/storage';
import { formatDate } from '../../utils/date';
import './About.css';

const About = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    bio: ''
  });

  useEffect(() => {
    const user = storage.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        bio: user.bio || ''
      });
      
      // 获取用户的文章
      const posts = storage.getPosts();
      const userPostsData = posts.filter(post => post.author === user.id);
      setUserPosts(userPostsData);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!currentUser) return;

    // 更新用户信息
    const users = storage.getUsers();
    const userIndex = users.findIndex(user => user.id === currentUser.id);
    
    if (userIndex !== -1) {
      const updatedUser = {
        ...users[userIndex],
        ...formData,
        updatedAt: new Date().toISOString()
      };
      
      users[userIndex] = updatedUser;
      storage.saveUsers(users);
      storage.setCurrentUser(updatedUser);
      setCurrentUser(updatedUser);
      setIsEditing(false);
      
      // 触发自定义事件
      window.dispatchEvent(new Event('userChange'));
      
      alert('个人信息更新成功！');
    }
  };

  const handleCancel = () => {
    setFormData({
      username: currentUser?.username || '',
      email: currentUser?.email || '',
      bio: currentUser?.bio || ''
    });
    setIsEditing(false);
  };

  if (!currentUser) {
    return (
      <div className="app">
        <Header />
        <main className="main">
          <div className="container-sm">
            <div className="about-guest">
              <h1>关于博客系统</h1>
              <div className="about-content">
                <h2>欢迎来到React博客系统</h2>
                <p>
                  这是一个基于React 19构建的现代化博客系统，支持Markdown编辑、
                  本地存储、响应式设计等功能。
                </p>
                
                <h3>主要特性</h3>
                <ul>
                  <li>📝 支持Markdown编辑和渲染</li>
                  <li>👤 用户注册和登录系统</li>
                  <li>📱 完全响应式设计</li>
                  <li>🎨 现代简约的UI界面</li>
                  <li>💾 本地数据持久化</li>
                  <li>🔍 文章搜索功能</li>
                  <li>📅 文章归档和分类</li>
                </ul>
                
                <h3>技术栈</h3>
                <ul>
                  <li>React 19 - 前端框架</li>
                  <li>React Router DOM - 路由管理</li>
                  <li>Markdown-it - Markdown解析</li>
                  <li>Highlight.js - 代码语法高亮</li>
                  <li>Anime.js - 动画效果</li>
                  <li>CSS3 - 样式系统</li>
                </ul>
                
                <div className="about-actions">
                  <a href="/auth" className="btn btn-primary">
                    开始写作
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <main className="main">
        <div className="container-sm">
          <div className="about-container">
            <div className="about-header">
              <img 
                src={currentUser.avatar} 
                alt={currentUser.username}
                className="about-avatar"
              />
              <h1>{currentUser.username}</h1>
              <p className="about-member-since">
                加入于 {formatDate(currentUser.createdAt, 'YYYY年MM月DD日')}
              </p>
            </div>

            <div className="about-content">
              {isEditing ? (
                <form onSubmit={handleSubmit} className="about-form">
                  <div className="form-group">
                    <label htmlFor="username">用户名</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bio">个人简介</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="介绍一下自己..."
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      保存
                    </button>
                    <button 
                      type="button" 
                      onClick={handleCancel}
                      className="btn btn-secondary"
                    >
                      取消
                    </button>
                  </div>
                </form>
              ) : (
                <div className="about-info">
                  <div className="info-item">
                    <span className="info-label">用户名</span>
                    <span className="info-value">{currentUser.username}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="info-label">邮箱</span>
                    <span className="info-value">{currentUser.email}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="info-label">个人简介</span>
                    <span className="info-value">
                      {currentUser.bio || '暂无简介'}
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="btn btn-primary"
                  >
                    编辑资料
                  </button>
                </div>
              )}
            </div>

            <div className="about-stats">
              <h3>我的统计</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{userPosts.length}</div>
                  <div className="stat-label">发布文章</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-number">
                    {userPosts.reduce((sum, post) => sum + (post.views || 0), 0)}
                  </div>
                  <div className="stat-label">总阅读量</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-number">
                    {userPosts.reduce((sum, post) => sum + (post.likes || 0), 0)}
                  </div>
                  <div className="stat-label">获得点赞</div>
                </div>
              </div>
            </div>

            {userPosts.length > 0 && (
              <div className="about-posts">
                <h3>我的文章</h3>
                <div className="posts-list">
                  {userPosts.map((post) => (
                    <div key={post.id} className="post-item">
                      <a href={`/post/${post.id}`} className="post-title">
                        {post.title}
                      </a>
                      <div className="post-meta">
                        <span>{formatDate(post.createdAt, 'YYYY-MM-DD')}</span>
                        <span>·</span>
                        <span>{post.views || 0} 阅读</span>
                        <span>·</span>
                        <span>{post.likes || 0} 点赞</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;